import React, { useState } from 'react';
import {
  Badge, Box, Button, Flex, FormControl, Heading, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Textarea, Tooltip,
  useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import { FiPlus, FiSlash, FiSend } from 'react-icons/fi';
import { FaFilePdf } from 'react-icons/fa';
import axios from 'axios';
import { previewPdf } from '../utils/openPdf';
import useCreditMemos from '../hooks/useCreditMemos';
import useCurrentUser from '../hooks/useCurrentUser';
import { useConfirm } from './ConfirmProvider';
import FieldLabel from './forms/FieldLabel';
import { money } from '../utils/money';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';

interface ProjectCreditMemosSectionProps {
  projectId: string;
  customerId?: string;
}

// item 1221 (Chad: "the credit memo shows up in invoices in the projects") — the project's
// customer credits: overpayments minted at deposit time plus manual adds (the QB legacies).
// Shows every memo for this CUSTOMER (a credit is account money — it shouldn't hide because
// it was filed under a sibling project). Mint/void = Pulse-admin, like void/archive.
// Renders NOTHING when the customer has no memos and the viewer can't mint.
const ProjectCreditMemosSection: React.FC<ProjectCreditMemosSectionProps> = ({ projectId, customerId }) => {
  const {
    memos, applications, create, voidMemo,
  } = useCreditMemos();
  const { canModerate } = useCurrentUser();
  const confirmDialog = useConfirm();
  const toast = useToast();
  const addModal = useDisclosure();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null); // item 1486
  const sub = useColorModeValue('gray.500', 'whiteAlpha.600');
  const rowBorder = useColorModeValue('gray.100', 'whiteAlpha.100');

  const mine = memos.filter((m) => m.customerId === customerId);
  if (mine.length === 0 && !canModerate) return null;

  const appliedFor = (memoId: string) => applications.filter((a) => a.creditMemoId === memoId);

  const add = async () => {
    const amt = parseFloat(amount);
    if (!customerId || !Number.isFinite(amt) || amt <= 0) {
      toast({ status: 'warning', title: 'Enter a positive credit amount.' });
      return;
    }
    setSaving(true);
    try {
      const r = await create({
        customerId, projectId, amount: amt, note: note.trim(),
      });
      toast({ status: 'success', title: `Credit memo ${r.number} added for ${money(amt)}.`, description: 'It offers itself on this customer’s next invoice finalize.' });
      addModal.onClose();
      setAmount('');
      setNote('');
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not add the credit', description: String(e?.response?.data ?? '') });
    } finally {
      setSaving(false);
    }
  };

  // item 1486 (Chad: "I can't send them a credit") — the credit memo document + a send-by-email
  // to the customer's accounting address (main email fallback), CC the collections address.
  const openPdf = (id: string) => previewPdf(`CreditMemo/${id}/pdf`).catch(() => toast({ status: 'error', title: 'Could not open the credit memo PDF' }));
  const doSend = async (id: string, number: string, remaining: number) => {
    if (!(await confirmDialog(`Email credit memo ${number} (${money(remaining)} available) to this customer's accounting email? It says the credit applies to their next invoice.`, { confirmLabel: 'Send credit memo' }))) return;
    setSendingId(id);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BASIX_API || ''}CreditMemo/${id}/send`, {});
      toast({ status: 'success', title: `${number} sent to ${data?.to ?? 'the customer'}.`, description: data?.cc ? `CC ${data.cc}` : undefined });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not send', description: String(e?.response?.data ?? '') });
    } finally {
      setSendingId(null);
    }
  };

  const doVoid = async (id: string, number: string) => {
    if (!(await confirmDialog(`Void credit memo ${number}? The customer's available credit drops by its remaining amount.`, { confirmLabel: 'Void memo' }))) return;
    try {
      await voidMemo(id);
      toast({ status: 'success', title: `${number} voided.` });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not void', description: String(e?.response?.data ?? '') });
    }
  };

  return (
    <Box mt={4}>
      <Flex align="center" mb={2} sx={{ gap: '8px' }}>
        <Heading size="xs" letterSpacing="0.06em">CUSTOMER CREDITS</Heading>
        {mine.length > 0 && (
          <Badge colorScheme="purple" fontSize="10px">
            {`${money(mine.reduce((s, m) => s + m.remaining, 0))} available`}
          </Badge>
        )}
        {canModerate && (
          <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={addModal.onOpen}>
            Add credit
          </Button>
        )}
      </Flex>
      {mine.length === 0 ? (
        <Text fontSize="xs" color={sub}>
          No credits on this customer. An overpaid deposit mints one automatically; Add credit
          records one by hand (e.g. a legacy QB overpayment).
        </Text>
      ) : (
        <Box>
          {mine.map((m) => (
            <Flex key={m.id} align="center" py={1.5} fontSize="sm" borderTopWidth="1px" borderTopColor={rowBorder} sx={{ gap: '10px' }}>
              <Text fontWeight="semibold" minW="60px">{m.number}</Text>
              <Text color={sub} fontSize="xs" minW="80px">{SqlDateTimeToDisplay(m.createdDate)}</Text>
              <Tooltip label={m.note || undefined} placement="top" hasArrow>
                <Text color={sub} fontSize="xs" isTruncated flex="1">{m.note || '—'}</Text>
              </Tooltip>
              {appliedFor(m.id).length > 0 && (
                <Badge fontSize="10px">{`applied ${money(m.amount - m.remaining)}`}</Badge>
              )}
              <Text fontWeight="medium" whiteSpace="nowrap">
                {m.remaining > 0 ? `${money(m.remaining)} available` : 'fully applied'}
              </Text>
              <Tooltip label="Credit memo PDF" placement="top" hasArrow>
                <Button size="xs" variant="ghost" aria-label={`${m.number} PDF`} onClick={() => openPdf(m.id)}>
                  <FaFilePdf />
                </Button>
              </Tooltip>
              {m.remaining > 0 && (
                <Tooltip label="Email this credit memo to the customer" placement="top" hasArrow>
                  <Button size="xs" variant="ghost" aria-label={`Send ${m.number}`} isLoading={sendingId === m.id} onClick={() => doSend(m.id, m.number, m.remaining)}>
                    <FiSend />
                  </Button>
                </Tooltip>
              )}
              {canModerate && m.remaining === m.amount && (
                <Tooltip label="Void this credit memo (Pulse admin)" placement="top" hasArrow>
                  <Button size="xs" variant="ghost" onClick={() => doVoid(m.id, m.number)}>
                    <FiSlash />
                  </Button>
                </Tooltip>
              )}
            </Flex>
          ))}
        </Box>
      )}

      <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add customer credit</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color={sub} mb={3}>
              Records money this customer already paid beyond what was invoiced (an overpayment).
              It shows on their statement and the AR report as a credit, and offers itself the
              next time you finalize one of their invoices.
            </Text>
            <FormControl mb={3} isRequired>
              <FieldLabel>Amount</FieldLabel>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Credit amount" />
            </FormControl>
            <FormControl>
              <FieldLabel>Note</FieldLabel>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Where it came from (e.g. legacy QB overpayment, check #)" rows={2} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={addModal.onClose}>Cancel</Button>
            <Button colorScheme="brandGreen" onClick={add} isLoading={saving}>Add credit</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

ProjectCreditMemosSection.defaultProps = { customerId: undefined };

export default ProjectCreditMemosSection;
